import { provide } from "@lit/context";
import { LitElement, PropertyValues } from "lit";
import { customElement, property } from "lit/decorators.js";
import { ProductSelectionContext, productSelectionContextKey } from "./product-selection/product-selection.context";
import { OrderManagementContext } from "./order-management/order-management.context";
import { orderManagementContextKey } from "./order-management/order-management.context";
import { AbpApplicationConfigurationService, AppConfigurationService, OrderDtoReadable, ApplicationConfigurationDto, RemoteServiceErrorResponse } from "./api";
import { getCookie } from "typescript-cookie";
import { client } from "./api/client.gen";
import dayjs from "dayjs";
import advancedFormat from "dayjs/plugin/advancedFormat";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

@customElement("app-root")
export class App extends LitElement {
    @provide({ context: orderManagementContextKey })
    private _orderManagementContext : OrderManagementContext = new OrderManagementContext({} as OrderDtoReadable);

    @provide({ context: productSelectionContextKey })
    private readonly _productSelectionContext = new ProductSelectionContext();

    @property({ type: Object })
    public initialOrderState: OrderDtoReadable | null = null;

    constructor() {
        super();
        dayjs.extend(advancedFormat);
        dayjs.extend(utc);
        dayjs.extend(timezone);

        AbpApplicationConfigurationService.get().then(this.handleApplicationConfigurationResponse.bind(this));

        client.interceptors.request.use((request, _options) => {
            const xsrfToken = getCookie("XSRF-TOKEN");

            if (xsrfToken) {
                request.headers.set("RequestVerificationToken", xsrfToken);
            }

            return request;
        });
    }

    override async connectedCallback() {
        super.connectedCallback();
    }

    protected override async firstUpdated(_changedProperties: PropertyValues): Promise<void> {
        super.firstUpdated(_changedProperties);
        let timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const { data: timeZoneData } = await AppConfigurationService.getVenueTimeZone();
        if(timeZoneData)
        {
            timeZone = timeZoneData;
        }

        this._productSelectionContext.init(timeZone);
        this._orderManagementContext.init(timeZone);

        if(this.initialOrderState) {
            // hydrate contexts with initial state if provided (e.g. from server-side rendering)
            this._orderManagementContext = new OrderManagementContext(this.initialOrderState, timeZone);
        }
    }

    protected override createRenderRoot() {
        return this;
    }

    private handleApplicationConfigurationResponse(value: (
          { data: ApplicationConfigurationDto; error: undefined; }
        | { data: undefined; error: RemoteServiceErrorResponse; })
        & { request: Request; response: Response; }) {
            console.log("Application configuration response:", value);
    }
}
